import React, { useMemo } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { RouterType, type PageLink } from '@common/Providers/GraphqlProvider/generated';
import { ROUTES_INTERNAL } from '@utils/constants';

import { normalizeUrl } from '@/components/views/Admin/utils/utils';

interface CustomLink extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  pageLink?: PageLink | null;
  children: React.ReactNode;
  subRouteName?: string;
  parentLink?: PageLink | null;
}

const CustomLink = (props: CustomLink) => {
  const { pageLink, children, href = '#', parentLink, ...restProps } = props;
  const searchParams = useSearchParams();
  const previewValue = searchParams.get('preview');

  const finalHref = useMemo(() => {
    // Función auxiliar para añadir el parámetro "preview" sin afectar otros searchParams
    const addPreview = (url: string) => {
      if (!previewValue) return url;

      try {
        const urlObj = new URL(url, window.location.origin);
        // Agregamos el parámetro si no existe
        if (!urlObj.searchParams.has('preview')) {
          urlObj.searchParams.append('preview', previewValue);
        }

        // Si la URL es del mismo origen, devolver solo el path + search
        if (urlObj.origin === window.location.origin) {
          return urlObj.pathname + urlObj.search;
        }

        // Si es un sitio externo, devolver la URL completa
        return urlObj.toString();
      } catch (err) {
        console.error('Error procesando la URL:', err);
        return url;
      }
    };

    // Evaluación según el tipo de ruta definido en pageLink
    if (pageLink?.routeType) {
      switch (pageLink.routeType) {
        case RouterType.InternaltRoute: {
          const internalUrl = pageLink.routeNavigation
            ? (ROUTES_INTERNAL[pageLink.routeNavigation] ?? '#')
            : '#';
          return addPreview(internalUrl);
        }
        case RouterType.ExternalRoute: {
          const externalUrl = normalizeUrl(pageLink.url ?? '#');
          return addPreview(externalUrl);
        }
        case RouterType.InternalRouteWithArguments:
          return addPreview(pageLink.url || '#');
        case RouterType.DynamicPage: {
          let dynamicUrl = '#';

          // Usar el slug de dynamicPageSlugs si está disponible
          const slug = pageLink.dynamicPageSlugs?.[0]?.slug;
          const urlValue = slug || pageLink.url;

          if (urlValue) {
            const cleanUrl = urlValue.startsWith('/') ? urlValue.slice(1) : urlValue;

            if (parentLink?.url && parentLink?.routeType === RouterType.Microsite) {
              // Si tiene parentLink de tipo Microsite, construir la ruta como /micro-site/parenturl/itemurl
              const cleanParentUrl = parentLink.url.startsWith('/')
                ? parentLink.url.slice(1)
                : parentLink.url;
              dynamicUrl = `/micro-site/${cleanParentUrl}/${cleanUrl}`;
            } else {
              // Si no tiene parentLink Microsite, usar la ruta normal de dynamic
              dynamicUrl = `/dynamic/${cleanUrl}`;
            }
          }

          return addPreview(dynamicUrl);
        }
        case RouterType.Microsite: {
          let micrositeUrl = '#';

          // Usar el slug de dynamicPageSlugs si está disponible
          const micrositeSlug = pageLink.dynamicPageSlugs?.[0]?.slug;
          const micrositeUrlValue = micrositeSlug || pageLink.url;

          if (micrositeUrlValue) {
            const cleanUrl = micrositeUrlValue.startsWith('/')
              ? micrositeUrlValue.slice(1)
              : micrositeUrlValue;

            if (parentLink?.url && parentLink?.routeType === RouterType.Microsite) {
              // Si tiene parentLink, construir la ruta como /micro-site/parenturl/itemurl
              const cleanParentUrl = parentLink.url.startsWith('/')
                ? parentLink.url.slice(1)
                : parentLink.url;
              micrositeUrl = `/micro-site/${cleanParentUrl}/${cleanUrl}`;
            } else {
              // Si no tiene parentLink, usar la ruta normal
              micrositeUrl = `/micro-site/${cleanUrl}`;
            }
          }

          return addPreview(micrositeUrl);
        }
        default:
          return addPreview(href);
      }
    }
    return addPreview(href);
  }, [pageLink, href, previewValue]);

  return (
    <Link {...restProps} target={pageLink?.target || undefined} href={finalHref}>
      {children}
    </Link>
  );
};

export default CustomLink;

// import React, { useMemo } from 'react'
// import Link from 'next/link'
// import { RouterType, type PageLink } from '@common/Providers/GraphqlProvider/generated'
// import { ROUTES_INTERNAL } from '@utils/constants'

// import { normalizeUrl } from '@/components/views/Admin/utils/utils'

// interface CustomLink extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
//   pageLink?: PageLink | null
//   children: React.ReactNode
//   subRouteName?: string
// }

// const CustomLink = (props: CustomLink) => {
//   const { pageLink, children, href = '#', ...restProps } = props

//   return useMemo(() => {
//     if (pageLink?.routeType) {
//       switch (pageLink.routeType) {
//         case RouterType.InternaltRoute:
//           return (
//             <Link
//               {...restProps}
//               href={
//                 pageLink.routeNavigation ? ROUTES_INTERNAL[pageLink?.routeNavigation] ?? '#' : '#'
//               }
//               target={pageLink.target || undefined}
//             >
//               {children}
//             </Link>
//           )
//         case RouterType.ExternalRoute:
//           const url = pageLink.url ?? '#'
//           // if (!url.includes('http')) {
//           //   url = `https://${url}`
//           // }
//           return (
//             <Link {...restProps} href={normalizeUrl(url)} target={pageLink.target || undefined}>
//               {children}
//             </Link>
//           )
//         case RouterType.InternalRouteWithArguments:
//           return (
//             <Link
//               {...restProps}
//               href={pageLink.routeNavigation || '#'}
//               target={pageLink.target || undefined}
//             >
//               {children}
//             </Link>
//           )
//         case RouterType.DynamicPage:
//           return (
//             <Link
//               {...restProps}
//               href={pageLink.url ? `/dynamic/${pageLink.url?.startsWith("/") ? pageLink.url.slice(1) : pageLink.url}` : '#'}
//               target={pageLink.target || undefined}
//             >
//               {children}
//             </Link>
//           )
//       }
//     }
//     return (
//       <>
//         <Link {...restProps} href={href}>
//           {children}
//         </Link>
//       </>
//     )
//   }, [])
// }

// export default CustomLink
